import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

// Importar API de relatórios
import { relatoriosApi } from '../../services/relatoriosApi';

// Configurar dayjs para português
dayjs.locale('pt-br');

// Comentários em português:
// Este módulo é carregado fora do React. Para obter o branding atual, vamos buscar o endpoint público sob demanda.
const getBrandingSync = async () => {
	try {
		const res = await fetch('/api/public/branding');
		if (!res.ok) return null;
		const json = await res.json();
		return json.data || null;
	} catch (e) {
		return null;
	}
};

/**
 * Classe responsável pela geração de PDFs no sistema
 * REFATORADA - Agora usa método simplificado similar ao ModalSenhasUsuario
 */
class PDFGenerator {
  /**
   * Gera um PDF usando window.print (método simplificado)
   * 
   * @param {Object} options Opções para geração do PDF
   * @param {string} options.tipoRelatorio Tipo do relatório (geral, servicos, etc)
   * @param {Object} options.dataInicio Data de início do período do relatório
   * @param {Object} options.dataFim Data de fim do período do relatório
   * @param {Array} options.metricasPrincipais Array contendo métricas principais
   * @param {Function} options.onSuccess Callback para sucesso
   * @param {Function} options.onError Callback para erro
   */
  gerarPDF = async (options) => {
    const {
      tipoRelatorio = 'geral',
      dataInicio,
      dataFim,
      metricasPrincipais = [],
      onSuccess,
      onError
    } = options;

    try {
      // Verificar se as datas são objetos dayjs válidos
      const dataInicioValida = dayjs(dataInicio).isValid() ? dayjs(dataInicio) : dayjs();
      const dataFimValida = dayjs(dataFim).isValid() ? dayjs(dataFim) : dayjs();
      
      console.log('Gerando PDF simplificado, aguarde...');
      
      // Obter dados para o relatório
      const dadosRelatorio = await this.obterDadosRelatorio({
        dataInicio: dataInicioValida,
        dataFim: dataFimValida,
        tipoRelatorio
      });
      
      // Buscar branding público (logos dinâmicos)
      const branding = await getBrandingSync();
      const logoHeader = branding?.assets?.reports?.header || branding?.assets?.panel?.main || '';
      const logoFooter = branding?.assets?.reports?.footer || logoHeader;
      const displayName = branding?.displayName || 'Sistema de Gerenciamento de Senhas';

      // Gerar PDF com método simplificado
      this.gerarPDFSimplificado({
        dados: dadosRelatorio,
        tipoRelatorio,
        dataInicio: dataInicioValida,
        dataFim: dataFimValida,
        metricasPrincipais,
        onSuccess,
        onError,
        logoHeader,
        logoFooter,
        displayName,
      });
      
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      if (typeof onError === 'function') {
        onError('Ocorreu um erro ao gerar o PDF: ' + error.message);
      }
    }
  };

  /**
   * Obtém dados necessários para o relatório a partir da API
   */
  obterDadosRelatorio = async (options) => {
    const { dataInicio, dataFim, tipoRelatorio } = options;
    
    try {
      // Verificar se as datas são válidas
      if (!dataInicio || !dataFim || !dayjs(dataInicio).isValid() || !dayjs(dataFim).isValid()) {
        console.error('Datas inválidas para relatório:', { dataInicio, dataFim });
        throw new Error('Datas inválidas para geração do relatório.');
      }
      
      // Montar os filtros para a API
      const filtros = {
        dataInicio: dataInicio.format('YYYY-MM-DD'),
        dataFim: dataFim.format('YYYY-MM-DD'),
        tipoRelatorio
      };
      
      console.log('Carregando dados reais da API para PDF com filtros:', filtros);
      
      // Buscar dados reais da API
      const [dadosMovimentoResponse, dadosServicoResponse, dadosAtendentesResponse, dadosUsuariosDetalhadosResponse] = await Promise.all([
        relatoriosApi.buscarMovimentoPorHorario(filtros),
        relatoriosApi.buscarDadosPorServico(filtros),
        relatoriosApi.buscarDadosAtendentes(filtros),
        relatoriosApi.buscarRelatorioUsuariosDetalhado(filtros)
      ]);
      
      console.log('[DEBUG] PDFGenerator - Resposta da API movimento por horário:', dadosMovimentoResponse);
      console.log('[DEBUG] PDFGenerator - Resposta da API dados por serviço:', dadosServicoResponse);
      console.log('[DEBUG] PDFGenerator - Resposta da API dados de atendentes:', dadosAtendentesResponse);
      console.log('[DEBUG] PDFGenerator - Resposta da API usuários detalhados:', dadosUsuariosDetalhadosResponse);
      
      // Processar dados de movimento por horário
      let distribuicaoPorHorario = [];
      if (dadosMovimentoResponse.success && dadosMovimentoResponse.data && dadosMovimentoResponse.data.movimentoPorHorario) {
        distribuicaoPorHorario = dadosMovimentoResponse.data.movimentoPorHorario.map(item => ({
          horario: item.hora,
          quantidade: item.quantidade
        }));
      }
      
      // Processar dados por serviço
      let distribuicaoPorServico = [];
      if (dadosServicoResponse.success && dadosServicoResponse.data) {
        if (typeof dadosServicoResponse.data === 'object' && !Array.isArray(dadosServicoResponse.data)) {
          distribuicaoPorServico = Object.entries(dadosServicoResponse.data).map(([nome, quantidade]) => ({
            nome,
            quantidade,
            percentual: 0,
            tempoMedioAtendimento: 0,
            tempoMedioEspera: 0
          }));
        } else if (Array.isArray(dadosServicoResponse.data)) {
          distribuicaoPorServico = dadosServicoResponse.data.map(item => ({
            nome: item.servico || item.nome,
            quantidade: item.quantidade,
            percentual: item.percentual || 0,
            tempoMedioAtendimento: item.tempoMedioAtendimento || 0,
            tempoMedioEspera: item.tempoMedioEspera || 0
          }));
        }
      }
      
      // Processar dados de atendentes
      let estatisticasUsuarios = [];
      if (dadosAtendentesResponse.success && dadosAtendentesResponse.data && dadosAtendentesResponse.data.atendentes) {
        estatisticasUsuarios = dadosAtendentesResponse.data.atendentes.map(atendente => {
          let tempoMedioAtendimento = 0;
          if (atendente.tempoMedioMs && typeof atendente.tempoMedioMs === 'number') {
            tempoMedioAtendimento = atendente.tempoMedioMs / (1000 * 60);
          } else if (atendente.tempoMedio && typeof atendente.tempoMedio === 'string') {
            const match = atendente.tempoMedio.match(/(\d+)/);
            if (match) {
              tempoMedioAtendimento = parseInt(match[1]);
            }
          }
          
          return {
            nome: atendente.nome || `Atendente #${atendente.id}`,
            totalAtendimentos: atendente.totalAtendimentos || 0,
            tempoMedioAtendimento: tempoMedioAtendimento,
            servicoMaisAtendido: atendente.servicoMaisAtendido || 'N/A'
          };
        });
      }

      // Processar dados detalhados de usuários
      let dadosUsuariosDetalhados = [];
      if (dadosUsuariosDetalhadosResponse.success && dadosUsuariosDetalhadosResponse.data) {
        dadosUsuariosDetalhados = dadosUsuariosDetalhadosResponse.data.map(usuario => ({
          nome: usuario.nome,
          atendimentos: usuario.atendimentos,
          tme: usuario.tme,
          tmd: usuario.tmd,
          tma: usuario.tma,
          tempoTotal: usuario.tempoTotal,
          tmeMs: usuario.tmeMs || 0,
          tmdMs: usuario.tmdMs || 0,
          tmaMs: usuario.tmaMs || 0,
          tempoTotalMs: usuario.tempoTotalMs || 0
        }));
      }
      
      // Calcular métricas consolidadas
      let totalSenhas = 0;
      let senhasConcluidas = 0;
      let senhasCanceladas = 0;
      let tempoMedioAtendimento = 0;
      let tempoMedioEspera = 0;
      let atendimentosPreferenciais = 0;
      
      // Somar senhas dos dados por horário
      if (distribuicaoPorHorario.length > 0) {
        totalSenhas = distribuicaoPorHorario.reduce((sum, item) => sum + (item.quantidade || 0), 0);
        senhasConcluidas = Math.floor(totalSenhas * 0.9);
        senhasCanceladas = totalSenhas - senhasConcluidas;
      }
      
      // Calcular tempo médio de atendimento dos serviços
      if (distribuicaoPorServico.length > 0) {
        const temposAtendimento = distribuicaoPorServico
          .map(s => s.tempoMedioAtendimento || 0)
          .filter(t => t > 0);
        
        if (temposAtendimento.length > 0) {
          tempoMedioAtendimento = temposAtendimento.reduce((sum, tempo) => sum + tempo, 0) / temposAtendimento.length;
        }
        
        const temposEspera = distribuicaoPorServico
          .map(s => s.tempoMedioEspera || 0)
          .filter(t => t > 0);
        
        if (temposEspera.length > 0) {
          tempoMedioEspera = temposEspera.reduce((sum, tempo) => sum + tempo, 0) / temposEspera.length;
        }
      }
      
      atendimentosPreferenciais = Math.floor(totalSenhas * 0.1);
      
      return {
        distribuicaoPorServico,
        distribuicaoPorHorario,
        estatisticasUsuarios,
        dadosUsuariosDetalhados,
        totalSenhas,
        senhasConcluidas,
        senhasAtendidas: senhasConcluidas,
        senhasCanceladas,
        tempoMedioAtendimento,
        tempoMedioEspera,
        atendimentosPreferenciais
      };
      
    } catch (error) {
      console.error('Erro ao obter dados para o relatório:', error);
      
      // Retornar estrutura mínima para evitar erros
      return {
        distribuicaoPorServico: [],
        distribuicaoPorHorario: [],
        estatisticasUsuarios: [],
        dadosUsuariosDetalhados: [],
        totalSenhas: 0,
        senhasConcluidas: 0,
        senhasAtendidas: 0,
        senhasCanceladas: 0,
        tempoMedioAtendimento: 0,
        tempoMedioEspera: 0,
        atendimentosPreferenciais: 0
      };
    }
  };

  /**
   * Gera um PDF usando método simplificado (window.print)
   */
  gerarPDFSimplificado = (options) => {
    const {
      dados,
      tipoRelatorio,
      dataInicio,
      dataFim,
      metricasPrincipais,
      onSuccess,
      onError,
      logoHeader = '',
      logoFooter = '',
      displayName = 'Sistema de Gerenciamento de Senhas',
    } = options;

    try {
      const printWindow = window.open('', '_blank');
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>Relatório ${tipoRelatorio.charAt(0).toUpperCase() + tipoRelatorio.slice(1)} - ${dataInicio.format('DD/MM/YYYY')} a ${dataFim.format('DD/MM/YYYY')}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                line-height: 1.6;
                color: #333;
                background: white;
                padding: 20px;
              }
              
              .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                text-align: center;
                margin-bottom: 30px;
                border-bottom: 3px solid #1976d2;
                padding-bottom: 20px;
                position: relative;
              }
              
              .header-logo {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 80px;
                margin-bottom: 15px;
              }
              
              .header-logo img {
                height: 40px;
                width: auto;
              }
              
              .header h1 {
                color: #1976d2;
                font-size: 28px;
                margin-bottom: 10px;
              }
              
              .header p {
                color: #666;
                font-size: 14px;
              }
              
              .periodo-info {
                background: #f8f9fa;
                border-radius: 8px;
                padding: 15px;
                margin-bottom: 30px;
                border-left: 4px solid #1976d2;
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              
              .periodo-info h3 {
                color: #1976d2;
                margin-bottom: 5px;
              }
              
              .periodo-info p {
                color: #666;
                font-size: 14px;
              }
              
              .metrics-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 20px;
                margin-bottom: 30px;
              }
              
              .metric-card {
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 8px;
                padding: 20px;
                text-align: center;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                position: relative;
                overflow: hidden;
              }
              
              .metric-card::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 4px;
                background: linear-gradient(90deg, #1976d2, #42a5f5);
              }
              
              .metric-card h3 {
                font-size: 32px;
                margin-bottom: 5px;
                font-weight: bold;
              }
              
              .metric-card p {
                color: #666;
                font-size: 14px;
                font-weight: 500;
              }
              
              .metric-card.primary h3 { color: #1976d2; }
              .metric-card.success h3 { color: #4caf50; }
              .metric-card.warning h3 { color: #ff9800; }
              .metric-card.error h3 { color: #f44336; }
              .metric-card.info h3 { color: #2196f3; }
              .metric-card.purple h3 { color: #9c27b0; }
              
              .section {
                margin-bottom: 40px;
              }
              
              .section-title {
                color: #1976d2;
                font-size: 20px;
                margin-bottom: 15px;
                padding-bottom: 10px;
                border-bottom: 2px solid #e0e0e0;
                display: flex;
                align-items: center;
                gap: 10px;
              }
              
              .section-title::before {
                content: '';
                width: 4px;
                height: 20px;
                background: #1976d2;
                border-radius: 2px;
              }
              
              .table-container {
                overflow-x: auto;
                margin-bottom: 20px;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 12px;
                background: white;
              }
              
              th, td {
                border: 1px solid #ddd;
                padding: 12px 8px;
                text-align: left;
              }
              
              th {
                background: #1976d2;
                color: white;
                font-weight: bold;
                text-align: center;
              }
              
              tr:nth-child(even) {
                background: #f8f9fa;
              }
              
              tr:hover {
                background: #e3f2fd;
              }
              
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              
              .highlight {
                background: #fff3cd;
                border: 1px solid #ffeaa7;
                padding: 15px;
                border-radius: 8px;
                margin: 20px 0;
              }
              
              .highlight h4 {
                color: #856404;
                margin-bottom: 10px;
              }
              
              .highlight p {
                color: #856404;
                margin: 5px 0;
              }
              
              .footer {
                margin-top: 40px;
                text-align: center;
                color: #666;
                font-size: 12px;
                border-top: 1px solid #ddd;
                padding-top: 20px;
              }
              
              .footer img {
                height: 20px;
                width: auto;
                margin-bottom: 10px;
              }
              
              .no-data {
                text-align: center;
                color: #666;
                font-style: italic;
                padding: 20px;
                background: #f8f9fa;
                border-radius: 8px;
                border: 1px solid #e0e0e0;
              }
              
              @media print {
                body {
                  padding: 0;
                }
                
                .metrics-grid {
                  grid-template-columns: repeat(3, 1fr);
                  gap: 15px;
                }
                
                .metric-card {
                  padding: 15px;
                }
                
                .metric-card h3 {
                  font-size: 24px;
                }
                
                table {
                  font-size: 10px;
                }
                
                th, td {
                  padding: 8px 6px;
                }
              }
              
              @page {
                margin: 2cm;
                size: A4;
              }
            </style>
          </head>
          <body>
            <!-- CABEÇALHO -->
            <div class="header">
              <div class="header-logo">
                ${logoHeader ? `<img src="${logoHeader}" alt="Logo" />` : ''}
                <div>
                  <h1>RELATÓRIO ${tipoRelatorio.toUpperCase()}</h1>
                  <p>${displayName}</p>
                </div>
              </div>
            </div>
            
            <!-- INFORMAÇÕES DO PERÍODO -->
            <div class="periodo-info">
              <div>
                <h3>Período do Relatório</h3>
                <p><strong>${dataInicio.format('DD/MM/YYYY')}</strong> até <strong>${dataFim.format('DD/MM/YYYY')}</strong></p>
              </div>
              <div>
                <h3>Gerado em</h3>
                <p>${dayjs().format('DD/MM/YYYY HH:mm')}</p>
              </div>
            </div>
            
            <!-- MÉTRICAS PRINCIPAIS -->
            <div class="section">
              <h2 class="section-title">📊 Métricas Principais</h2>
              <div class="metrics-grid">
                <div class="metric-card primary">
                  <h3>${dados.totalSenhas}</h3>
                  <p>Total de Senhas</p>
                </div>
                <div class="metric-card success">
                  <h3>${dados.senhasConcluidas}</h3>
                  <p>Senhas Concluídas</p>
                </div>
                <div class="metric-card error">
                  <h3>${dados.senhasCanceladas}</h3>
                  <p>Senhas Canceladas</p>
                </div>
                <div class="metric-card warning">
                  <h3>${Math.round(dados.tempoMedioAtendimento)} min</h3>
                  <p>Tempo Médio de Atendimento</p>
                </div>
                <div class="metric-card info">
                  <h3>${Math.round(dados.tempoMedioEspera)} min</h3>
                  <p>Tempo Médio de Espera</p>
                </div>
                <div class="metric-card purple">
                  <h3>${dados.atendimentosPreferenciais}</h3>
                  <p>Atendimentos Preferenciais</p>
                </div>
              </div>
            </div>
            
            <!-- DISTRIBUIÇÃO POR HORÁRIO (TABELA) -->
            <div class="section">
              <h2 class="section-title">🕐 Movimento por Horário</h2>
              ${dados.distribuicaoPorHorario && dados.distribuicaoPorHorario.length > 0 ? `
                <div class="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Horário</th>
                        <th>Quantidade de Senhas</th>
                        <th>Percentual do Total</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${dados.distribuicaoPorHorario.map(item => {
                        const percentual = dados.totalSenhas > 0 ? ((item.quantidade / dados.totalSenhas) * 100).toFixed(1) : '0.0';
                        const status = item.quantidade > (dados.totalSenhas / dados.distribuicaoPorHorario.length) ? 'Alto' : 
                                      item.quantidade > 0 ? 'Normal' : 'Baixo';
                        return `
                          <tr>
                            <td class="text-center"><strong>${item.horario}</strong></td>
                            <td class="text-center">${item.quantidade}</td>
                            <td class="text-center">${percentual}%</td>
                            <td class="text-center">${status}</td>
                          </tr>
                        `;
                      }).join('')}
                    </tbody>
                  </table>
                </div>
                <div class="highlight">
                  <h4>📈 Análise do Movimento por Horário</h4>
                  <p><strong>Horário de maior movimento:</strong> ${dados.distribuicaoPorHorario.reduce((max, item) => item.quantidade > max.quantidade ? item : max, dados.distribuicaoPorHorario[0]).horario} (${dados.distribuicaoPorHorario.reduce((max, item) => item.quantidade > max.quantidade ? item : max, dados.distribuicaoPorHorario[0]).quantidade} senhas)</p>
                  <p><strong>Horário de menor movimento:</strong> ${dados.distribuicaoPorHorario.reduce((min, item) => item.quantidade < min.quantidade ? item : min, dados.distribuicaoPorHorario[0]).horario} (${dados.distribuicaoPorHorario.reduce((min, item) => item.quantidade < min.quantidade ? item : min, dados.distribuicaoPorHorario[0]).quantidade} senhas)</p>
                  <p><strong>Média por horário:</strong> ${(dados.totalSenhas / dados.distribuicaoPorHorario.length).toFixed(1)} senhas</p>
                </div>
              ` : `
                <div class="no-data">
                  <p>Nenhum dado de movimento por horário disponível para o período selecionado.</p>
                </div>
              `}
            </div>
            
            <!-- DISTRIBUIÇÃO POR SERVIÇOS (TABELA) -->
            <div class="section">
              <h2 class="section-title">🏢 Distribuição por Serviços</h2>
              ${dados.distribuicaoPorServico && dados.distribuicaoPorServico.length > 0 ? `
                <div class="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Serviço</th>
                        <th>Quantidade</th>
                        <th>Percentual</th>
                        <th>Tempo Médio Atendimento</th>
                        <th>Tempo Médio Espera</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${dados.distribuicaoPorServico.map(item => `
                        <tr>
                          <td><strong>${item.nome}</strong></td>
                          <td class="text-center">${item.quantidade}</td>
                          <td class="text-center">${item.percentual ? item.percentual.toFixed(1) : '0.0'}%</td>
                          <td class="text-center">${item.tempoMedioAtendimento ? Math.round(item.tempoMedioAtendimento) + ' min' : 'N/A'}</td>
                          <td class="text-center">${item.tempoMedioEspera ? Math.round(item.tempoMedioEspera) + ' min' : 'N/A'}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </div>
                <div class="highlight">
                  <h4>🏆 Análise dos Serviços</h4>
                  <p><strong>Serviço mais procurado:</strong> ${dados.distribuicaoPorServico.reduce((max, item) => item.quantidade > max.quantidade ? item : max, dados.distribuicaoPorServico[0]).nome} (${dados.distribuicaoPorServico.reduce((max, item) => item.quantidade > max.quantidade ? item : max, dados.distribuicaoPorServico[0]).quantidade} atendimentos)</p>
                  <p><strong>Total de tipos de serviços:</strong> ${dados.distribuicaoPorServico.length}</p>
                </div>
              ` : `
                <div class="no-data">
                  <p>Nenhum dado de distribuição por serviços disponível para o período selecionado.</p>
                </div>
              `}
            </div>
            
            <!-- DESEMPENHO DOS ATENDENTES (TABELA) -->
            <div class="section">
              <h2 class="section-title">👥 Desempenho dos Atendentes</h2>
              ${dados.estatisticasUsuarios && dados.estatisticasUsuarios.length > 0 ? `
                <div class="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Atendente</th>
                        <th>Total de Atendimentos</th>
                        <th>Tempo Médio por Atendimento</th>
                        <th>Serviço Mais Atendido</th>
                        <th>Produtividade</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${dados.estatisticasUsuarios.map((usuario, index) => {
                        const produtividade = usuario.totalAtendimentos > 50 ? 'Alta' : 
                                            usuario.totalAtendimentos > 20 ? 'Média' : 'Baixa';
                        return `
                          <tr>
                            <td><strong>${usuario.nome}</strong></td>
                            <td class="text-center">${usuario.totalAtendimentos}</td>
                            <td class="text-center">${Math.round(usuario.tempoMedioAtendimento)} min</td>
                            <td class="text-center">${usuario.servicoMaisAtendido}</td>
                            <td class="text-center">${produtividade}</td>
                          </tr>
                        `;
                      }).join('')}
                    </tbody>
                  </table>
                </div>
                <div class="highlight">
                  <h4>🎯 Análise de Desempenho</h4>
                  <p><strong>Atendente mais produtivo:</strong> ${dados.estatisticasUsuarios.reduce((max, item) => item.totalAtendimentos > max.totalAtendimentos ? item : max, dados.estatisticasUsuarios[0]).nome} (${dados.estatisticasUsuarios.reduce((max, item) => item.totalAtendimentos > max.totalAtendimentos ? item : max, dados.estatisticasUsuarios[0]).totalAtendimentos} atendimentos)</p>
                  <p><strong>Tempo médio geral:</strong> ${Math.round(dados.estatisticasUsuarios.reduce((sum, item) => sum + item.tempoMedioAtendimento, 0) / dados.estatisticasUsuarios.length)} min por atendimento</p>
                  <p><strong>Total de atendentes ativos:</strong> ${dados.estatisticasUsuarios.length}</p>
                </div>
              ` : `
                <div class="no-data">
                  <p>Nenhum dado de desempenho dos atendentes disponível para o período selecionado.</p>
                </div>
              `}
            </div>
            
            <!-- DADOS DETALHADOS DOS USUÁRIOS (TABELA) -->
            ${dados.dadosUsuariosDetalhados && dados.dadosUsuariosDetalhados.length > 0 ? `
              <div class="section">
                <h2 class="section-title">📋 Análise Detalhada dos Atendentes</h2>
                <div class="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Atendente</th>
                        <th>Atendimentos</th>
                        <th>TME (Tempo Médio Espera)</th>
                        <th>TMD (Tempo Médio Deslocamento)</th>
                        <th>TMA (Tempo Médio Atendimento)</th>
                        <th>Tempo Total Médio</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${dados.dadosUsuariosDetalhados.map(usuario => `
                        <tr>
                          <td><strong>${usuario.nome}</strong></td>
                          <td class="text-center">${usuario.atendimentos}</td>
                          <td class="text-center">${usuario.tme || 'N/A'}</td>
                          <td class="text-center">${usuario.tmd || 'N/A'}</td>
                          <td class="text-center">${usuario.tma || 'N/A'}</td>
                          <td class="text-center">${usuario.tempoTotal || 'N/A'}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </div>
                <div class="highlight">
                  <h4>⚡ Análise de Eficiência</h4>
                  <p><strong>Legenda:</strong></p>
                  <p>• <strong>TME:</strong> Tempo entre emissão da senha e chamada do cliente</p>
                  <p>• <strong>TMD:</strong> Tempo entre chamada e início do atendimento (deslocamento)</p>
                  <p>• <strong>TMA:</strong> Tempo efetivo de atendimento ao cliente</p>
                </div>
              </div>
            ` : ''}
            
            <!-- RESUMO EXECUTIVO -->
            <div class="section">
              <h2 class="section-title">📈 Resumo Executivo</h2>
              <div class="highlight">
                <h4>Principais Indicadores do Período</h4>
                <p><strong>Volume total:</strong> ${dados.totalSenhas} senhas processadas</p>
                <p><strong>Taxa de conclusão:</strong> ${dados.totalSenhas > 0 ? ((dados.senhasConcluidas / dados.totalSenhas) * 100).toFixed(1) : '0'}%</p>
                <p><strong>Taxa de cancelamento:</strong> ${dados.totalSenhas > 0 ? ((dados.senhasCanceladas / dados.totalSenhas) * 100).toFixed(1) : '0'}%</p>
                <p><strong>Atendimentos preferenciais:</strong> ${dados.totalSenhas > 0 ? ((dados.atendimentosPreferenciais / dados.totalSenhas) * 100).toFixed(1) : '0'}% do total</p>
                ${dados.estatisticasUsuarios.length > 0 ? `
                  <p><strong>Produtividade média:</strong> ${Math.round(dados.estatisticasUsuarios.reduce((sum, item) => sum + item.totalAtendimentos, 0) / dados.estatisticasUsuarios.length)} atendimentos por pessoa</p>
                ` : ''}
              </div>
            </div>
            
            <!-- RODAPÉ -->
            <div class="footer">
              ${logoFooter ? `<img src="${logoFooter}" alt="Logo" />` : ''}
              <p>${displayName} - Relatório gerado automaticamente</p>
              <p>© ${new Date().getFullYear()} - Todos os direitos reservados</p>
              <p>Gerado em ${dayjs().format('DD/MM/YYYY HH:mm:ss')}</p>
            </div>
          </body>
        </html>
      `;

      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Aguarda o carregamento e imprime
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      
      if (typeof onSuccess === 'function') {
          onSuccess('Relatório gerado com sucesso! A impressão foi iniciada automaticamente.');
      }
      };
      
    } catch (error) {
      console.error('Erro no método simplificado:', error);
      
      if (typeof onError === 'function') {
        onError('Não foi possível gerar o relatório: ' + error.message);
      }
    }
  };
}

// Exportar uma instância única do gerador de PDF
export default new PDFGenerator(); 